/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `EventCreationDetailsSection` for the Caintra Connect frontend.
 * @module components/EventCreation/EventCreationDetailsSection
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import Select from "react-select";
import { getEventRegisterCategoryOptions } from "../../data/selectOptions";
import { getEventFieldBorderColor } from "../../utils/eventCreation/eventCreationValidation.js";
import { getNullableNumberInputValue } from "../../utils/eventCreation/eventCreationFormFields.js";

const EventCreationDetailsSection = ({
  t,
  styles,
  formData,
  showError,
  selectCategoryRef,
  categorySelectStyles,
  onInputChange,
  onCategorySelectChange,
}) => {
  const eventRegisterCategoryOptions = getEventRegisterCategoryOptions(t);

  return (
  <div className={styles.detailsContainer}>
    <div className={`${styles.formRow} ${styles.rowEventName}`}>
      <div className={styles.nameCol}>
        <label className={styles.label}>
          {t("pages.eventCreation.eventName")}
        </label>
        <input
          type="text"
          name="nombre"
          value={formData.nombre}
          onChange={onInputChange}
          className={styles.input}
          style={{
            borderColor: getEventFieldBorderColor(showError, formData.nombre),
          }}
          placeholder={t("pages.eventCreation.eventNamePlaceholder")}
        />
      </div>
      <div className={`${styles.formCol} ${styles.eventDateCol}`}>
        <label className={styles.label}>
          {t("pages.eventCreation.eventDate")}
        </label>
        <input
          type="date"
          name="fechaEvento"
          placeholder={t("common.dateFormatPlaceholder")}
          className={styles.input}
          style={{
            borderColor: getEventFieldBorderColor(
              showError,
              formData.fechaEvento,
            ),
          }}
          value={formData.fechaEvento}
          onChange={onInputChange}
        />
      </div>
    </div>
    <div className={`${styles.formRow} ${styles.rowAudience}`}>
      <div className={`${styles.formCol} ${styles.eventTypeCol}`}>
        <label className={styles.label}>
          {t("pages.eventCreation.category")}
        </label>
        <Select
          ref={selectCategoryRef}
          options={eventRegisterCategoryOptions}
          isSearchable={false}
          value={eventRegisterCategoryOptions.find(
            (option) => option.value === formData.categoria,
          )}
          onChange={onCategorySelectChange}
          placeholder={t("pages.eventCreation.categoryPlaceholder")}
          styles={categorySelectStyles}
          getOptionValue={(option) => option.value}
          formatOptionLabel={(option) => <span>{option.label}</span>}
        />
      </div>
      <div className={`${styles.formCol} ${styles.timeCol}`}>
        <label className={styles.label}>{t("pages.eventCreation.time")}</label>
        <input
          type="time"
          name="hora"
          value={formData.hora}
          onChange={onInputChange}
          className={styles.input}
          style={{
            borderColor: getEventFieldBorderColor(showError, formData.hora),
          }}
          placeholder={t("common.timeExamplePlaceholder")}
        />
      </div>
      <div className={`${styles.formCol} ${styles.durationCol}`}>
        <label className={styles.label}>
          {t("pages.eventCreation.duration")}
        </label>
        <div className={styles.durationInputContainer}>
          <input
            type="text"
            name="duracion"
            pattern="[0-9]{2}:[0-9]{2}"
            value={formData.duracion || "00:00"}
            onChange={onInputChange}
            className={styles.input}
            style={{
              borderColor: getEventFieldBorderColor(
                showError,
                formData.duracion,
              ),
            }}
            placeholder={t("common.durationPlaceholder")}
          />
          <span className={styles.inputLabel}>{t("common.hoursUnit")}</span>
        </div>
      </div>
    </div>
    <div className={styles.formRow}>
      <div className={styles.descriptionCol}>
        <label className={styles.label}>
          {t("pages.eventCreation.description")}
        </label>
        <textarea
          name="descripcion"
          value={formData.descripcion}
          onChange={onInputChange}
          className={styles.textarea}
          style={{
            borderColor: getEventFieldBorderColor(
              showError,
              formData.descripcion,
            ),
          }}
          placeholder={t("pages.eventCreation.descriptionPlaceholder")}
        />
      </div>
    </div>
    <div className={styles.formRow}>
      <div className={styles.formCol}>
        <label className={styles.label}>
          {t("pages.eventCreation.membersDate")}
        </label>
        <input
          type="date"
          name="fechaDisponibleSocios"
          placeholder={t("common.dateFormatPlaceholder")}
          className={styles.input}
          style={{
            borderColor: getEventFieldBorderColor(
              showError,
              formData.fechaDisponibleSocios,
            ),
          }}
          value={formData.fechaDisponibleSocios || ""}
          onChange={onInputChange}
        />
      </div>
      <div className={styles.formCol}>
        <label className={styles.label}>
          {t("pages.eventCreation.nonMembersDate")}
        </label>
        <input
          type="date"
          name="fechaDisponibleNoSocios"
          placeholder={t("common.dateFormatPlaceholder")}
          className={styles.input}
          style={{
            borderColor: getEventFieldBorderColor(
              showError,
              formData.fechaDisponibleNoSocios,
            ),
          }}
          value={formData.fechaDisponibleNoSocios || ""}
          onChange={onInputChange}
        />
      </div>
      <div className={styles.formCol}>
        <label className={styles.label}>
          {t("pages.eventCreation.dueDateRegistration")}
        </label>
        <input
          type="date"
          name="fechaLimiteRegistro"
          placeholder={t("common.dateFormatPlaceholder")}
          className={styles.input}
          style={{
            borderColor: getEventFieldBorderColor(
              showError,
              formData.fechaLimiteRegistro,
            ),
          }}
          value={formData.fechaLimiteRegistro || ""}
          onChange={onInputChange}
        />
      </div>
    </div>
    <div className={styles.formRow}>
      <div className={styles.costCol}>
        <label className={styles.label}>
          {t("pages.eventCreation.membersCost")}
        </label>
        <input
          type="number"
          name="costoSocios"
          min="0"
          step="0.01"
          value={getNullableNumberInputValue(formData.costoSocios)}
          style={{
            borderColor: getEventFieldBorderColor(
              showError,
              formData.costoSocios,
            ),
          }}
          onChange={onInputChange}
          className={styles.input}
          placeholder={t("pages.eventCreation.costPlaceholder")}
        />
      </div>
      <div className={styles.costCol}>
        <label className={styles.label}>
          {t("pages.eventCreation.nonMembersCost")}
        </label>
        <input
          type="number"
          name="costoNoSocios"
          min="0"
          step="0.01"
          value={getNullableNumberInputValue(formData.costoNoSocios)}
          style={{
            borderColor: getEventFieldBorderColor(
              showError,
              formData.costoNoSocios,
            ),
          }}
          onChange={onInputChange}
          className={styles.input}
          placeholder={t("pages.eventCreation.costPlaceholder")}
        />
      </div>
    </div>
    <div className={styles.formRow}>
      <div className={styles.locationCol}>
        <label className={styles.label}>
          {t("pages.eventCreation.location")}
        </label>
        <input
          type="text"
          name="ubicacion"
          value={formData.ubicacion}
          onChange={onInputChange}
          className={styles.input}
          style={{
            borderColor: getEventFieldBorderColor(showError, formData.ubicacion),
          }}
          placeholder={t("pages.eventCreation.locationPlaceholder")}
        />
      </div>
      <div className={styles.costCol}>
        <label className={styles.label}>
          {t("pages.eventCreation.maximumCapacity")}
        </label>
        <input
          type="number"
          name="cupoMaximo"
          min={0}
          step={1}
          value={formData.cupoMaximo}
          onChange={onInputChange}
          className={styles.input}
          style={{
            borderColor: getEventFieldBorderColor(showError, formData.cupoMaximo),
          }}
          placeholder={t("pages.eventCreation.maximumCapacityPlaceholder")}
        />
      </div>
    </div>
    <div className={`${styles.formRow} ${styles.rowCategoryDuration}`}>
      <div className={`${styles.formCol} ${styles.categoryCol}`}>
        <label className={styles.label}>
          {t("pages.eventCreation.category")}
        </label>
        <Select
          ref={selectCategoryRef}
          options={eventRegisterCategoryOptions}
          isSearchable={false}
          value={eventRegisterCategoryOptions.find(
            (option) => option.value === formData.categoria,
          )}
          onChange={onCategorySelectChange}
          placeholder={t("pages.eventCreation.categoryPlaceholder")}
          styles={categorySelectStyles}
          getOptionValue={(option) => option.value}
          formatOptionLabel={(option) => <span>{option.label}</span>}
        />
      </div>
      <div className={`${styles.formCol} ${styles.durationCol}`}>
        <label className={styles.label}>
          {t("pages.eventCreation.duration")}
        </label>
        <div className={styles.durationInputContainer}>
          <input
            type="text"
            name="duracion"
            pattern="[0-9]{2}:[0-9]{2}"
            value={formData.duracion || "00:00"}
            onChange={onInputChange}
            className={styles.input}
            style={{
              borderColor: getEventFieldBorderColor(
                showError,
                formData.duracion,
              ),
            }}
            placeholder={t("common.durationPlaceholder")}
          />
          <span className={styles.inputLabel}>{t("common.hoursUnit")}</span>
        </div>
      </div>
    </div>
  </div>
  );
};

export default EventCreationDetailsSection;
